import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Trash2, Loader2, CheckCircle2, AlertCircle, Lock, Save, User as UserIcon } from 'lucide-react'
import api from '../../lib/axios'
import { useAuthStore } from '../../stores/authStore'

const errMsg = (e) =>
  e?.response?.data?.message || 'Une erreur est survenue. Réessayez.'
const fieldErr = (e, k) => e?.response?.data?.errors?.[k]?.[0]

const BTN_PRIMARY =
  'inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#006b5f] text-white text-sm font-semibold ' +
  'hover:bg-[#005a50] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

/* ============================================================
   PETITS COMPOSANTS
   ============================================================ */
function Alert({ type, children }) {
  if (!children) return null
  const ok = type === 'success'
  const Icon = ok ? CheckCircle2 : AlertCircle
  return (
    <div
      className={`flex items-start gap-2 text-sm rounded-xl px-3 py-2 ${
        ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
      }`}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  )
}

function Field({ label, error, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input
        {...props}
        className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#006b5f]/20 focus:border-[#006b5f] ${
          error ? 'border-red-300' : 'border-gray-200'
        }`}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  )
}

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 space-y-4">
      <h2 className="flex items-center gap-2 text-base font-semibold text-navy">
        <Icon className="w-5 h-5" /> {title}
      </h2>
      {children}
    </section>
  )
}

/* ============================================================
   PAGE PROFIL
   ============================================================ */
export default function Profile() {
  const navigate = useNavigate()
  const fileRef = useRef(null)
  const logout = useAuthStore((s) => s.logout)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [user, setUser] = useState(null)

  // Infos
  const [form, setForm] = useState({ name: '', email: '', phone: '', locale: 'fr' })
  const [infoState, setInfoState] = useState({ saving: false, ok: '', error: '', errors: {} })

  // Avatar
  const [avatarState, setAvatarState] = useState({ busy: false, error: '' })

  // Mot de passe
  const emptyPwd = { current_password: '', password: '', password_confirmation: '' }
  const [pwd, setPwd] = useState(emptyPwd)
  const [pwdState, setPwdState] = useState({ saving: false, ok: '', error: '', errors: {} })

  // Suppression
  const [delPwd, setDelPwd] = useState('')
  const [delState, setDelState] = useState({ busy: false, error: '' })

  const applyUser = (u) => {
    setUser(u)
    setForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      locale: u.locale || 'fr',
    })

    // Synchronise le store global (sidebar, etc.)
    try {
      useAuthStore.setState((s) => ({ user: { ...s.user, ...u } }))
    } catch { /* ignore */ }

    // Synchronise l'utilisateur mémorisé localement
    try {
      const raw = localStorage.getItem('tapio_user')
      if (raw) {
        localStorage.setItem('tapio_user', JSON.stringify({ ...JSON.parse(raw), ...u }))
      }
    } catch { /* ignore */ }
  }

  useEffect(() => {
    let cancelled = false
    api
      .get('/profile')
      .then((r) => {
        if (!cancelled) applyUser(r.data.user)
      })
      .catch((e) => {
        if (cancelled) return
        const status = e?.response?.status
        setLoadError(
          status === 404
            ? 'Route /profile introuvable (404). Lancez : php artisan route:clear'
            : status === 401
            ? 'Session expirée. Reconnectez-vous.'
            : !e?.response
            ? 'Serveur injoignable. Vérifiez votre connexion ou réessayez plus tard.'
            : errMsg(e)
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveInfo = async (e) => {
    e.preventDefault()
    setInfoState({ saving: true, ok: '', error: '', errors: {} })
    try {
      const { data } = await api.put('/profile', form)
      applyUser(data.user)
      setInfoState({ saving: false, ok: data.message, error: '', errors: {} })
    } catch (err) {
      setInfoState({
        saving: false,
        ok: '',
        error: errMsg(err),
        errors: err?.response?.data?.errors || {},
      })
    }
  }

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setAvatarState({ busy: false, error: 'Image trop lourde (max 2 Mo).' })
      return
    }
    setAvatarState({ busy: true, error: '' })
    try {
      const fd = new FormData()
      fd.append('avatar', file)
      const { data } = await api.post('/profile/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      applyUser(data.user)
      setAvatarState({ busy: false, error: '' })
    } catch (err) {
      setAvatarState({ busy: false, error: fieldErr(err, 'avatar') || errMsg(err) })
    }
  }

  const removeAvatar = async () => {
    setAvatarState({ busy: true, error: '' })
    try {
      const { data } = await api.delete('/profile/avatar')
      applyUser(data.user)
      setAvatarState({ busy: false, error: '' })
    } catch (err) {
      setAvatarState({ busy: false, error: errMsg(err) })
    }
  }

  const savePwd = async (e) => {
    e.preventDefault()
    setPwdState({ saving: true, ok: '', error: '', errors: {} })
    try {
      const { data } = await api.put('/profile/password', pwd)
      setPwd(emptyPwd)
      setPwdState({ saving: false, ok: data.message, error: '', errors: {} })
    } catch (err) {
      setPwdState({
        saving: false,
        ok: '',
        error: errMsg(err),
        errors: err?.response?.data?.errors || {},
      })
    }
  }

  const deleteAccount = async (e) => {
    e.preventDefault()
    if (!window.confirm('Cette action est irréversible. Supprimer votre compte ?')) return
    setDelState({ busy: true, error: '' })
    try {
      await api.delete('/profile', { data: { password: delPwd } })
      try {
        await logout?.()
      } catch {
        /* tokens déjà révoqués côté serveur */
      }
      localStorage.removeItem('tapio_token')
      localStorage.removeItem('tapio_user')
      navigate('/login')
    } catch (err) {
      setDelState({ busy: false, error: fieldErr(err, 'password') || errMsg(err) })
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto">
        <Alert type="error">{loadError || 'Impossible de charger le profil.'}</Alert>
      </div>
    )
  }

  const initials = (user.name || '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-navy">Mon profil</h1>
        <p className="text-sm text-gray-500">
          Gérez vos informations personnelles et votre sécurité.
        </p>
      </header>

      {/* Avatar + identité */}
      <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover border border-gray-100"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#006b5f] text-white flex items-center justify-center text-xl font-semibold">
                {initials}
              </div>
            )}
            {avatarState.busy && (
              <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-navy truncate">{user.name}</p>
            <p className="text-sm text-gray-500 truncate">{user.email}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {user.is_premium && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full">
                  ⭐ PREMIUM
                </span>
              )}
              {!user.email_verified && (
                <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-bold rounded-full">
                  Email non vérifié
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={uploadAvatar}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={avatarState.busy}
            className={BTN_PRIMARY}
          >
            <Camera className="w-4 h-4" /> Changer la photo
          </button>
          {user.avatar && (
            <button
              type="button"
              onClick={removeAvatar}
              disabled={avatarState.busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          )}
        </div>
        <div className="mt-3">
          <Alert type="error">{avatarState.error}</Alert>
        </div>
      </section>

      {/* Infos personnelles */}
      <Card title="Informations personnelles" icon={UserIcon}>
        <form onSubmit={saveInfo} className="space-y-4">
          <Field
            label="Nom complet"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={infoState.errors.name?.[0]}
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={infoState.errors.email?.[0]}
            required
          />
          <Field
            label="Téléphone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            error={infoState.errors.phone?.[0]}
          />
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Langue</span>
            <select
              value={form.locale}
              onChange={(e) => setForm({ ...form, locale: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006b5f]/20 focus:border-[#006b5f]"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </label>

          <Alert type="success">{infoState.ok}</Alert>
          <Alert type="error">
            {infoState.error && !Object.keys(infoState.errors).length ? infoState.error : ''}
          </Alert>

          <button type="submit" disabled={infoState.saving} className={BTN_PRIMARY}>
            {infoState.saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}{' '}
            Enregistrer
          </button>
        </form>
      </Card>

      {/* Mot de passe */}
      <Card title="Mot de passe" icon={Lock}>
        <form onSubmit={savePwd} className="space-y-4">
          <Field
            label="Mot de passe actuel"
            type="password"
            autoComplete="current-password"
            value={pwd.current_password}
            onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
            error={pwdState.errors.current_password?.[0]}
            required
          />
          <Field
            label="Nouveau mot de passe (8 caractères min., lettres + chiffres)"
            type="password"
            autoComplete="new-password"
            value={pwd.password}
            onChange={(e) => setPwd({ ...pwd, password: e.target.value })}
            error={pwdState.errors.password?.[0]}
            required
          />
          <Field
            label="Confirmer le nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            value={pwd.password_confirmation}
            onChange={(e) => setPwd({ ...pwd, password_confirmation: e.target.value })}
            required
          />

          <Alert type="success">{pwdState.ok}</Alert>
          <Alert type="error">
            {pwdState.error && !Object.keys(pwdState.errors).length ? pwdState.error : ''}
          </Alert>

          <button type="submit" disabled={pwdState.saving} className={BTN_PRIMARY}>
            {pwdState.saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}{' '}
            Modifier le mot de passe
          </button>
        </form>
      </Card>

      {/* Zone danger */}
      {user.role !== 'super_admin' && (
        <section className="bg-white rounded-2xl border border-red-100 p-5 sm:p-6 space-y-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-red-600">
            <Trash2 className="w-5 h-5" /> Supprimer mon compte
          </h2>
          <p className="text-sm text-gray-500">
            Votre compte sera désactivé et vous serez déconnecté de tous les appareils.
          </p>
          <form onSubmit={deleteAccount} className="space-y-3">
            <Field
              label="Confirmez avec votre mot de passe"
              type="password"
              value={delPwd}
              onChange={(e) => setDelPwd(e.target.value)}
              required
            />
            <Alert type="error">{delState.error}</Alert>
            <button
              type="submit"
              disabled={delState.busy || !delPwd}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {delState.busy && <Loader2 className="w-4 h-4 animate-spin" />} Supprimer définitivement
            </button>
          </form>
        </section>
      )}
    </div>
  )
}